import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import SweetAlert from "react-bootstrap-sweetalert";
import { useHistory } from "react-router-dom";
// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
// @material-ui/icons
import Box from "@material-ui/core/Box";
import Person from "@material-ui/icons/Person";
import Edit from "@material-ui/icons/Edit";
import Close from "@material-ui/icons/Close";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import TableSortLabel from "@material-ui/core/TableSortLabel";
// core components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardIcon from "components/Card/CardIcon.js";
import CardHeader from "components/Card/CardHeader.js";
import Loader from "components/Loader/Loader.js";
import { PAGINATION_DETAILS, PAGE_SIZES } from "../../common/constants";
import {
  FETCH_COMPANY_REQUEST,
  DELETE_COMPANY_REQUEST,
} from "../../redux/company/actions";
import { useComposeDispatcher } from "../../common/hooks";
import styles from "assets/jss/material-dashboard-pro-react/views/userStyle.js";

const useStyles = makeStyles(styles);

const columns = [
  { id: "name", label: "Name", minWidth: 100, sort: true },
  { id: "emailAddress", label: "Email", minWidth: 130, sort: false },
  { id: "address", label: "Address", minWidth: 170, sort: false },
  { id: "status", label: "Status", minWidth: 100, sort: true },
  {
    id: "createdOn",
    label: "Date Created",
    minWidth: 130,
    type: "date",
    sort: true,
  },
  {
    id: "action",
    label: "Action",
    minWidth: 170,
    align: "right",
    sort: false,
    format: (value) => value.toFixed(2),
  },
];

export default function Company() {
  const history = useHistory();
  const { company } = useSelector((state) => ({
    company: state.company,
  }));
  const [alert, setAlert] = React.useState(null);

  const [order, setOrder] = React.useState("DESC");
  const [orderBy, setOrderBy] = React.useState("createdOn");

  const [getCompanyList] = useComposeDispatcher(FETCH_COMPANY_REQUEST);
  const [deleteCompany] = useComposeDispatcher(DELETE_COMPANY_REQUEST);

  const classes = useStyles();

  const [page, setPage] = React.useState(PAGINATION_DETAILS.pageNumber);
  const [rowsPerPage, setRowsPerPage] = React.useState(
    PAGINATION_DETAILS.pageSize
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    getCompanyList({
      pageNumber: newPage,
      pageSize: rowsPerPage,
      sortBy: orderBy,
      sortOrder: order,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  useEffect(() => {
    getCompanyList({
      pageNumber: page,
      pageSize: rowsPerPage,
      sortBy: orderBy,
      sortOrder: order,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createSortHandler = (property) => () => {
    const isAsc = orderBy === property && order === "ASC";
    setOrder(isAsc ? "DESC" : "ASC");
    setOrderBy(property);
    getCompanyList({
      pageNumber: 0,
      pageSize: rowsPerPage,
      sortBy: property,
      sortOrder: isAsc ? "DESC" : "ASC",
    });
    setPage(0);
  };

  const warningWithConfirmMessage = (company) => {
    setAlert(
      <SweetAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title="Are you sure?"
        onConfirm={() => {
          deleteCompany({
            supplierId: company.supplierId,
            pageNumber: page,
            pageSize: rowsPerPage,
            sortBy: orderBy,
            sortOrder: order,
          });
          hideAlert();
        }}
        onCancel={() => hideAlert()}
        confirmBtnCssClass={classes.button + " " + classes.success}
        cancelBtnCssClass={classes.button + " " + classes.danger}
        confirmBtnText="Yes, delete it!"
        cancelBtnText="Cancel"
        showCancel
      >
        You will not be able to recover <b>{company.name}</b> company data!
      </SweetAlert>
    );
  };

  // const successDelete = () => {
  //   setAlert(
  //     <SweetAlert
  //       success
  //       style={{ display: "block", marginTop: "-100px" }}
  //       title="Deleted!"
  //       onConfirm={() => hideAlert()}
  //       onCancel={() => hideAlert()}
  //       confirmBtnCssClass={classes.button + " " + classes.success}
  //     >
  //       Your imaginary file has been deleted.
  //     </SweetAlert>
  //   );
  // };

  const hideAlert = () => {
    setAlert(null);
  };

  const onEditButtonClick = ({ supplierId }) => {
    history.push(`/admin/edit-company/${supplierId}`);
  };

  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="danger" icon>
            <CardIcon color="danger">
              <Person />
            </CardIcon>
            <Box className={classes.cardIconTitle} style={{ float: "right" }}>
              <Button
                color="primary"
                onClick={() => {
                  history.push(`/admin/add-company`);
                }}
              >
                Create Company
              </Button>
            </Box>
          </CardHeader>
          <CardBody>
            <TableContainer className={classes.container}>
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        style={{ maxWidth: column.minWidth }}
                      >
                        {column.sort ? (
                          <TableSortLabel
                            style={{
                              maxWidth: column.minWidth,
                              minWidth: column.minWidth,
                            }}
                            active={orderBy === column.id}
                            direction={
                              orderBy === column.id
                                ? order.toLowerCase()
                                : "asc"
                            }
                            onClick={createSortHandler(column.id)}
                          >
                            <Typography>{column.label}</Typography>
                          </TableSortLabel>
                        ) : (
                          <Typography>{column.label}</Typography>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {company.companyList.map((row, index) => {
                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={index + "" + row.userId}
                      >
                        {columns.map((column) => {
                          let value = row[column.id];
                          if (column.id === "address") {
                            let {
                              streetAddress1,
                              streetAddress2,
                              city,
                              state,
                              country,
                              zipcode,
                              primaryPhone,
                              secondaryPhone,
                            } = row["contactInfo"];
                            value = `
                            ${streetAddress1 ? streetAddress1 : ""}
                            ${streetAddress2 ? streetAddress2 : ""}
                            ${city ? city : ""}
                            ${state ? state : ""} 
                            ${country ? country : ""} 
                            ${zipcode ? zipcode : ""} 
                            ${primaryPhone ? primaryPhone : ""} 
                            ${secondaryPhone ? secondaryPhone : ""}
                          `;
                          }
                          if (column.type === "date") {
                            value = moment(row[column.id]).format(
                              "DD MMM, YYYY"
                            );
                          }
                          return (
                            <TableCell
                              key={column.id}
                              style={{ maxWidth: column.minWidth }}
                              align={column.align}
                            >
                              {column.id !== "action" &&
                              column.format &&
                              typeof value === "number"
                                ? column.format(value)
                                : value}
                              {column.id === "action" && (
                                <>
                                  <Button
                                    color="success"
                                    simple
                                    onClick={() => {
                                      onEditButtonClick(row);
                                    }}
                                    className={classes.actionButton}
                                  >
                                    <Edit />
                                  </Button>
                                  <Button
                                    color="danger"
                                    simple
                                    onClick={() => {
                                      warningWithConfirmMessage(row);
                                    }}
                                    className={classes.actionButton}
                                  >
                                    <Close />
                                  </Button>
                                </>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={PAGE_SIZES}
              component="div"
              count={company.totalElements}
              rowsPerPage={rowsPerPage}
              page={page}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
            <Loader status={company.loading} />
            {alert}
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
